import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, Subscription, firstValueFrom } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  ApiResponse,
  NotificationRecord,
  UnreadCountResponse,
} from '../../models/notification-center.model';
import { SocketService } from '../socket/socket.service';
import { ToastService } from '../toast/toast.service';

type NotificationFilterMode = 'all' | 'unread' | 'read';

interface InitializeOptions {
  defaultFilter?: NotificationFilterMode;
  eventNames?: string[];
}

@Injectable({
  providedIn: 'root',
})
export class NotificationCenterService implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly socketService = inject(SocketService);
  private readonly toastService = inject(ToastService);
  private readonly ngZone = inject(NgZone);

  private readonly notificationsSubject = new BehaviorSubject<NotificationRecord[]>([]);
  private readonly unreadCountSubject = new BehaviorSubject<number>(0);
  // null = retention is off, so the panel says nothing about ageing rather than
  // promising a purge no job is performing.
  private readonly retentionDaysSubject = new BehaviorSubject<number | null>(null);
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly errorSubject = new BehaviorSubject<string | null>(null);
  private readonly selectedNotificationSubject = new BehaviorSubject<NotificationRecord | null>(null);
  // Opens on Unread, not All.
  //
  // The bell showed a dot, which promised "there is something new". Landing on
  // All answers a question nobody asked — the three new rows sit buried in
  // several hundred already-read ones, and the reader has to find them. GitHub
  // and Linear both open their inbox unread-first for the same reason.
  //
  // Nothing is hidden: All is one tap away, and when there is nothing unread
  // the empty state reads "You're all caught up", which is the right answer to
  // "what's new?" rather than a broken-looking blank.
  private readonly filterSubject = new BehaviorSubject<NotificationFilterMode>('unread');
  private readonly subscriptions = new Subscription();
  private readonly pendingReadRequests = new Set<string>();
  // Filter-INDEPENDENT record of the notification ids this session has seen.
  // The visible list is filter-projected (unread/read modes drop entries), so
  // membership there cannot answer "is this delivery new?" — a re-delivered,
  // already-read notification is absent from the unread list yet is not new.
  //
  // Bounded FIFO: a long-lived tab on a high-volume account would otherwise
  // grow this set for the life of the page. Set preserves insertion order, so
  // eviction is "drop the oldest". Past the cap a re-delivered ancient id can
  // toast again — an acceptable trade against unbounded growth.
  private readonly seenNotificationIds = new Set<string>();
  private static readonly SEEN_IDS_CAP = 5000;

  private isRealtimeRegistered = false;
  private wasDisconnected = false;
  private currentFilter: NotificationFilterMode = 'unread';
  private userId: string | null = null;

  get notifications$(): Observable<NotificationRecord[]> {
    return this.notificationsSubject.asObservable();
  }

  get retentionDays$(): Observable<number | null> {
    return this.retentionDaysSubject.asObservable();
  }

  get unreadCount$(): Observable<number> {
    return this.unreadCountSubject.asObservable();
  }

  get loading$(): Observable<boolean> {
    return this.loadingSubject.asObservable();
  }

  get errors$(): Observable<string | null> {
    return this.errorSubject.asObservable();
  }

  get selectedNotification$(): Observable<NotificationRecord | null> {
    return this.selectedNotificationSubject.asObservable();
  }

  get filter$(): Observable<NotificationFilterMode> {
    return this.filterSubject.asObservable();
  }

  initialize(userId: string, options?: InitializeOptions): void {
    if (!userId?.trim()) {
      this.errorSubject.next('Unable to initialise notifications without a user identifier.');
      return;
    }

    const sanitizedUserId = userId.trim();
    // A different user in the same tab (switch without a full reload) must not
    // inherit the previous user's seen-ids — their notifications would arrive
    // pre-suppressed.
    if (this.userId && this.userId !== sanitizedUserId) {
      this.seenNotificationIds.clear();
    }
    this.userId = sanitizedUserId;
    this.setFilterInternal(options?.defaultFilter ?? 'unread', false);

    this.fetchNotifications();
    this.refreshUnreadCount();
    this.refreshRetentionWindow();
    this.registerRealtime(options?.eventNames);
  }

  refreshNotifications(): void {
    this.fetchNotifications();
  }

  refreshUnreadCount(): void {
    if (!this.userId) {
      return;
    }

    const params = new HttpParams().set('userId', this.userId);
    void firstValueFrom(
      this.http.get<ApiResponse<UnreadCountResponse>>(this.buildUrl('notifications/unread/count'), {
        params,
      })
    )
      .then((response) => {
        const unreadCount = Number(response?.data?.unreadCount ?? 0);
        this.unreadCountSubject.next(Number.isFinite(unreadCount) ? unreadCount : 0);
      })
      .catch((error) => this.handleRequestError('Failed to load unread notification count', error));
  }

  selectNotification(notification: NotificationRecord | null): void {
    this.selectedNotificationSubject.next(notification);

    if (notification && !notification.readAt) {
      this.markNotificationAsRead(notification);
    }
  }

  /**
   * Mark one notification read WITHOUT opening it.
   *
   * Distinct from selectNotification, which marks read as a side effect of
   * going somewhere. Some notifications are read the moment you see the title
   * — "export ready", "booking confirmed" — and forcing a round trip through
   * the record to clear them is why backlogs build up in the first place.
   */
  markAsRead(notification: NotificationRecord): void {
    if (!notification?.id || notification.readAt) return;
    this.markNotificationAsRead(notification);
  }

  trackByNotificationId(_: number, notification: NotificationRecord): string {
    return notification.id;
  }

  setFilter(mode: NotificationFilterMode): void {
    this.setFilterInternal(mode, true);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private fetchNotifications(): void {
    if (!this.userId) {
      return;
    }

    this.loadingSubject.next(true);
    let params = new HttpParams().set('userId', this.userId);

    if (this.currentFilter === 'unread') {
      params = params.set('isRead', 'false');
    } else if (this.currentFilter === 'read') {
      params = params.set('isRead', 'true');
    }

    void firstValueFrom(
      this.http.get<ApiResponse<NotificationRecord[]>>(this.buildUrl('notifications'), {
        params,
      })
    )
      .then((response) => {
        const notifications = Array.isArray(response?.data) ? response.data : [];
        const sortedNotifications = this.sortNotifications(notifications);
        // Oldest-first so the FIFO cap evicts the LEAST recent ids: the list is
        // sorted newest-first, and the recent ids are the ones a re-delivery
        // could actually reference.
        for (let i = sortedNotifications.length - 1; i >= 0; i--) {
          this.markNotificationSeen(sortedNotifications[i]?.id);
        }
        this.notificationsSubject.next(sortedNotifications);
        this.errorSubject.next(null);

        const currentSelection = this.selectedNotificationSubject.value;
        if (!currentSelection) {
          this.selectedNotificationSubject.next(sortedNotifications[0] ?? null);
        } else {
          const stillPresent = sortedNotifications.some((item) => item.id === currentSelection.id);
          if (!stillPresent) {
            this.selectedNotificationSubject.next(sortedNotifications[0] ?? null);
          }
        }
      })
      .catch((error) => this.handleRequestError('Failed to load notifications', error))
      .finally(() => this.loadingSubject.next(false));
  }

  private registerRealtime(eventNames?: string[]): void {
    if (this.isRealtimeRegistered) {
      return;
    }

    const channels = this.normaliseEventNames(eventNames);
    if (!channels.length) {
      return;
    }

    const connectionSubscription = this.socketService.connectionStatus$
      .pipe(
        catchError((error) => {
          this.handleRequestError('Failed to observe socket connection status', error);
          return EMPTY;
        })
      )
      .subscribe((isConnected) => {
        if (!isConnected) {
          this.wasDisconnected = true;
          return;
        }

        if (!this.isRealtimeRegistered) {
          channels.forEach((eventName) => {
            const subscription = this.socketService
              .listen<NotificationRecord>(eventName)
              .pipe(
                catchError((error) => {
                  this.handleRequestError(`Socket listener error for event "${eventName}"`, error);
                  return EMPTY;
                })
              )
              .subscribe((payload) => this.handleIncomingNotification(payload));

            this.subscriptions.add(subscription);
          });

          this.isRealtimeRegistered = true;
        } else if (this.wasDisconnected) {
          // Reconnected after a gap: recover anything delivered while offline
          // and reconcile the optimistic unread count against the server.
          this.fetchNotifications();
          this.refreshUnreadCount();
        }

        this.wasDisconnected = false;
      });

    this.subscriptions.add(connectionSubscription);
  }

  private handleIncomingNotification(notification: NotificationRecord | null | undefined): void {
    if (!notification) {
      return;
    }

    const isNew = !!notification.id && !this.seenNotificationIds.has(notification.id);
    this.markNotificationSeen(notification.id);

    const { wasUnreadBefore, isUnreadAfter } = this.updateNotificationList(notification);
    // Optimistic +1 only for a genuinely new arrival — a re-delivered id (which
    // may be absent from a filtered visible list) must not inflate the badge;
    // the server count refresh reconciles everything else.
    if (isNew && !wasUnreadBefore && isUnreadAfter) {
      this.incrementUnreadCount();
    }

    // Re-delivery of a notification this session has already seen (server
    // retry, overlapping producers, reconnect) updates the list silently —
    // only a genuinely NEW id earns a toast.
    if (!isNew) {
      return;
    }

    // Ensure the toast is rendered inside Angular's zone
    this.ngZone.run(() => {
      // Export progress/outcome is already shown by the header status pill (Preparing… →
      // Download complete / Email sent / failed). Don't double up with a top-right toast — the
      // notification still lands in the bell list for later reference. (Vipin, 2026-07-22)
      const meta = notification.metadata as { type?: unknown } | null;
      const metaType = typeof meta?.type === 'string' ? meta.type : '';
      // Suppress ONLY export progress/outcome (the header pill covers those). Prefer the typed
      // metadata; the title fallback matches only the worker's actual titles ('Export Ready' /
      // 'Export Failed', optionally 'Work Order …') — NOT any title starting with 'Export', so a
      // genuine alert like 'Export limit reached' still surfaces.
      const isExportNotification = metaType.startsWith('export')
        || /^(work order )?export (ready|failed)\b/i.test(notification.title ?? '');
      if (isExportNotification) {
        return;
      }
      const title = notification.title?.trim() || 'New notification';
      this.toastService.showInfo(title);
    });
  }

  private markNotificationAsRead(notification: NotificationRecord): void {
    if (!notification.id || this.pendingReadRequests.has(notification.id)) {
      return;
    }

    this.pendingReadRequests.add(notification.id);

    void firstValueFrom(
      this.http.patch<ApiResponse<NotificationRecord>>(
        this.buildUrl(`notifications/${notification.id}/read`),
        {}
      )
    )
      .then((response) => {
        const updated = response?.data;
        if (!updated) {
          return;
        }

        const { wasUnreadBefore, isUnreadAfter } = this.updateNotificationList(updated);
        if (wasUnreadBefore && !isUnreadAfter) {
          this.decrementUnreadCount();
        }
      })
      .catch((error) => this.handleRequestError('Failed to mark notification as read', error))
      .finally(() => this.pendingReadRequests.delete(notification.id));
  }

  /**
   * Mark every unread notification for this user as read, in one request.
   *
   * Server-side and unscoped by page: the backlog the badge counts is not the
   * page the panel is showing, so clearing only what is loaded would leave the
   * count stubbornly non-zero.
   */
  async markAllAsRead(): Promise<void> {
    if (!this.userId) return;

    try {
      await firstValueFrom(
        this.http.patch<ApiResponse<{ updated: number }>>(
          this.buildUrl('notifications/read-all'),
          { userId: this.userId },
        )
      );
      // Re-read rather than patching locally: the update covered rows this
      // client has never seen, so no local edit could be accurate.
      this.fetchNotifications();
      this.refreshUnreadCount();
    } catch (error) {
      this.handleRequestError('Failed to mark all notifications as read', error);
    }
  }

  /**
   * Remove one notification for good.
   *
   * Distinct from marking it read: read keeps it in history, dismiss says the
   * reader never wants to see it again.
   */
  /**
   * Dismiss a whole group at once — the "Clear" on each day heading.
   *
   * One request, not one per row: a day with fifty notifications is exactly the
   * per-row problem `markAllAsRead` was added to solve, and doing it from the
   * client would just move the same fifty round-trips somewhere less visible.
   */
  async dismissMany(notificationsToDismiss: NotificationRecord[]): Promise<void> {
    const ids = notificationsToDismiss.map((n) => n?.id).filter((id): id is string => !!id);
    if (ids.length === 0 || !this.userId) return;

    const idSet = new Set(ids);
    const unreadRemoved = notificationsToDismiss.filter((n) => !n.readAt).length;

    // Optimistic, same as the single dismiss: the group leaves immediately and
    // comes back whole if the request fails.
    const previous = this.notificationsSubject.value;
    this.notificationsSubject.next(previous.filter((n) => !idSet.has(n.id)));
    for (let i = 0; i < unreadRemoved; i += 1) this.decrementUnreadCount();

    try {
      await firstValueFrom(
        this.http.post<ApiResponse<unknown>>(this.buildUrl('notifications/dismiss'), {
          userId: this.userId,
          ids,
        })
      );
    } catch (error) {
      this.notificationsSubject.next(previous);
      if (unreadRemoved > 0) this.refreshUnreadCount();
      this.handleRequestError('Failed to clear notifications', error);
    }
  }

  async dismiss(notification: NotificationRecord): Promise<void> {
    if (!notification?.id) return;

    const wasUnread = !notification.readAt;
    // Optimistic: the row leaves the list immediately, and comes back if the
    // request fails. A dismiss that visibly lags feels broken.
    const previous = this.notificationsSubject.value;
    this.notificationsSubject.next(previous.filter((n) => n.id !== notification.id));
    if (wasUnread) this.decrementUnreadCount();

    try {
      await firstValueFrom(
        this.http.delete<ApiResponse<unknown>>(this.buildUrl(`notifications/${notification.id}`))
      );
    } catch (error) {
      this.notificationsSubject.next(previous);
      if (wasUnread) this.refreshUnreadCount();
      this.handleRequestError('Failed to dismiss notification', error);
    }
  }

  private updateNotificationList(
    notification: NotificationRecord
  ): { updatedRecord: NotificationRecord; wasUnreadBefore: boolean; isUnreadAfter: boolean } {
    const current = this.notificationsSubject.value;
    const existingIndex = current.findIndex((item) => item.id === notification.id);

    let wasUnreadBefore = false;
    let mergedNotification: NotificationRecord;
    let nextList: NotificationRecord[];

    if (existingIndex > -1) {
      wasUnreadBefore = !current[existingIndex].readAt;
      mergedNotification = {
        ...current[existingIndex],
        ...notification,
      };
      nextList = [...current];
      nextList[existingIndex] = mergedNotification;
    } else {
      mergedNotification = { ...notification };
      nextList = [mergedNotification, ...current];
    }

    let updatedList = nextList;
    if (this.currentFilter === 'unread') {
      updatedList = nextList.filter((item) => !item.readAt);
    } else if (this.currentFilter === 'read') {
      updatedList = nextList.filter((item) => !!item.readAt);
    }

    const sortedList = this.sortNotifications(updatedList);
    this.notificationsSubject.next(sortedList);

    if (this.selectedNotificationSubject.value?.id === mergedNotification.id) {
      const selectionExists = sortedList.some((item) => item.id === mergedNotification.id);
      this.selectedNotificationSubject.next(selectionExists ? mergedNotification : sortedList[0] ?? null);
    }

    return {
      updatedRecord: mergedNotification,
      wasUnreadBefore,
      isUnreadAfter: !mergedNotification.readAt,
    };
  }

  /** Records an id as seen, evicting the oldest entry once the cap is reached. */
  private markNotificationSeen(id: string | null | undefined): void {
    if (!id || this.seenNotificationIds.has(id)) {
      return;
    }
    this.seenNotificationIds.add(id);
    if (this.seenNotificationIds.size > NotificationCenterService.SEEN_IDS_CAP) {
      const oldest = this.seenNotificationIds.values().next().value;
      if (oldest !== undefined) {
        this.seenNotificationIds.delete(oldest);
      }
    }
  }

  private incrementUnreadCount(): void {
    const nextValue = this.unreadCountSubject.value + 1;
    this.unreadCountSubject.next(nextValue);
  }

  private decrementUnreadCount(): void {
    const nextValue = Math.max(this.unreadCountSubject.value - 1, 0);
    this.unreadCountSubject.next(nextValue);
  }

  private setFilterInternal(mode: NotificationFilterMode, shouldRefetch: boolean): void {
    const normalised = mode ?? 'all';

    this.currentFilter = normalised;
    this.filterSubject.next(this.currentFilter);

    if (shouldRefetch) {
      this.fetchNotifications();
      this.refreshUnreadCount();
    }
  }

  private sortNotifications(notifications: NotificationRecord[]): NotificationRecord[] {
    return [...notifications].sort((left, right) => {
      const leftTime = new Date(left.createdAt ?? '').getTime();
      const rightTime = new Date(right.createdAt ?? '').getTime();
      if (Number.isNaN(leftTime) && Number.isNaN(rightTime)) {
        return 0;
      }
      if (Number.isNaN(leftTime)) {
        return 1;
      }
      if (Number.isNaN(rightTime)) {
        return -1;
      }
      return rightTime - leftTime;
    });
  }

  private normaliseEventNames(eventNames?: string[]): string[] {
    const defaults = ['notification:created', 'notification', 'newNotification'];
    const customEvents = Array.isArray(eventNames) ? eventNames : [];
    const merged = [...defaults, ...customEvents];
    return merged
      .map((name) => name?.trim())
      .filter((name, index, array): name is string => !!name && array.indexOf(name) === index);
  }

  /**
   * Ask the API what the retention window is, once per session.
   *
   * Deliberately silent on failure: a panel that cannot reach this endpoint
   * should show no retention line at all, not an error and not a guess.
   */
  private refreshRetentionWindow(): void {
    firstValueFrom(
      this.http.get<ApiResponse<{ retentionDays: number | null }>>(this.buildUrl('notifications/retention'))
    )
      .then((response) => {
        const days = Number(response?.data?.retentionDays ?? 0);
        this.retentionDaysSubject.next(Number.isFinite(days) && days > 0 ? days : null);
      })
      .catch(() => this.retentionDaysSubject.next(null));
  }

  private buildUrl(path: string): string {
    const baseUrl =  environment.nodeApiUrl;
    if (!baseUrl) {
      throw new Error('Notification API base URL is not configured.');
    }

    const sanitisedBase = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
    const sanitisedPath = path.startsWith('/') ? path.slice(1) : path;
    return `${sanitisedBase}${sanitisedPath}`;
  }

  private handleRequestError(operation: string, error: unknown): void {
    const message = this.extractErrorMessage(error);
    const composedMessage = `${operation}. ${message}`;
    console.error(`[NotificationCenterService] ${composedMessage}`, error);
    this.errorSubject.next(composedMessage);
  }

  private extractErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const backendMessage =
        (typeof error.error === 'string' && error.error) ||
        (error.error?.message as string) ||
        error.message;
      return backendMessage || 'The server returned an unexpected response.';
    }

    if (error instanceof Error) {
      return error.message;
    }

    return 'An unexpected error occurred.';
  }
}
